import React, { useMemo, useState } from "react";
import { Box, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";

import DashboardSummaryStrip from "../../../../../components/Dashboard/DashboardSummaryStrip";
import Contract from "../../Contract";
import PricingUpdates from "../../PricingUpdates";
import Timesheets from "../../Timesheets";

const OperationsSection = ({
  adminUid,
  busyById,
  contracts = [],
  contractorProfilesByUid,
  contractors = [],
  formatCurrency,
  formatDate,
  hireRequestContracts = [],
  loading,
  manageContractRows = [],
  onMoveStatus,
  onApproveMatchedContract,
  onReturnMatchedContract,
  onConfirmExternalSignatures,
  onRequestRateChange,
  onResolveTimesheet,
  openTimesheets = [],
  pricingRequests = [],
  resolveContractorName,
  resolvePricing,
  resolveUserLabel,
  statusColor,
}) => {
  const [activeTab, setActiveTab] = useState("hire_requests");

  const summaryItems = useMemo(
    () => [
      {
        label: "Hire requests",
        value: hireRequestContracts.length,
        helper: "New and in-review requests",
      },
      {
        label: "Contracts",
        value: manageContractRows.length,
        helper: "Active engagements",
      },
      {
        label: "Timesheets",
        value: openTimesheets.length,
        helper: "Awaiting admin decision",
      },
      {
        label: "Pricing updates",
        value: pricingRequests.length,
        helper: "Pending rate changes",
      },
    ],
    [hireRequestContracts.length, manageContractRows.length, openTimesheets.length, pricingRequests.length]
  );

  const tabs = [
    {
      key: "hire_requests",
      label: "Hire requests",
      icon: <AssignmentOutlinedIcon fontSize="small" />,
    },
    {
      key: "contracts",
      label: "Contracts",
      icon: <RequestQuoteOutlinedIcon fontSize="small" />,
    },
    {
      key: "timesheets",
      label: "Timesheets",
      icon: <TaskOutlinedIcon fontSize="small" />,
    },
    {
      key: "pricing",
      label: "Pricing",
      icon: <PriceChangeOutlinedIcon fontSize="small" />,
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="h5" sx={{ fontWeight: 850 }}>
          Operations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage hire requests, active contracts, timesheet approvals, and pricing updates from one workspace.
        </Typography>
      </Stack>

      <DashboardSummaryStrip items={summaryItems} />

      <Paper variant="outlined" sx={{ borderColor: "#dadce0", borderRadius: 2, overflow: "hidden" }}>
        <Tabs
          value={activeTab}
          onChange={(_event, nextTab) => setActiveTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 56,
            borderBottom: "1px solid #edf0f3",
            "& .MuiTab-root": {
              minHeight: 56,
              fontWeight: 800,
              textTransform: "none",
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </Tabs>

        <Box sx={{ p: { xs: 1.5, md: 2 } }}>
          {activeTab === "hire_requests" && (
            <Contract
              loading={loading}
              title="Hire Requests"
              showStatusFilter={false}
              showAssignmentFilter={false}
              defaultAssignmentFilter="all"
              defaultStatusFilter="all"
              contracts={hireRequestContracts}
              contractors={contractors}
              adminUid={adminUid}
              busyById={busyById}
              statusColor={statusColor}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              onMoveStatus={onMoveStatus}
              onApproveMatchedContract={onApproveMatchedContract}
              onReturnMatchedContract={onReturnMatchedContract}
              onConfirmExternalSignatures={onConfirmExternalSignatures}
              onRequestRateChange={onRequestRateChange}
              resolveUserLabel={resolveUserLabel}
              contractorProfilesByUid={contractorProfilesByUid}
            />
          )}

          {activeTab === "contracts" && (
            <Contract
              loading={loading}
              title="Manage Contracts"
              showStatusFilter={false}
              showAssignmentFilter={false}
              defaultAssignmentFilter="mine"
              defaultStatusFilter="active"
              contracts={manageContractRows}
              contractors={contractors}
              adminUid={adminUid}
              busyById={busyById}
              statusColor={statusColor}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              onMoveStatus={onMoveStatus}
              onApproveMatchedContract={onApproveMatchedContract}
              onReturnMatchedContract={onReturnMatchedContract}
              onConfirmExternalSignatures={onConfirmExternalSignatures}
              onRequestRateChange={onRequestRateChange}
              resolveUserLabel={resolveUserLabel}
              contractorProfilesByUid={contractorProfilesByUid}
            />
          )}

          {activeTab === "timesheets" && (
            <Timesheets
              loading={loading}
              timesheets={openTimesheets}
              busyById={busyById}
              onResolve={onResolveTimesheet}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              resolveUserLabel={resolveUserLabel}
            />
          )}

          {activeTab === "pricing" && (
            <PricingUpdates
              loading={loading}
              pricingRequests={pricingRequests}
              contracts={contracts}
              busyById={busyById}
              resolvePricing={resolvePricing}
              formatCurrency={formatCurrency}
              resolveUserLabel={resolveUserLabel}
              resolveContractorFullName={resolveContractorName}
            />
          )}
        </Box>
      </Paper>
    </Stack>
  );
};

export default OperationsSection;
